import {
  Button,
  Icon,
  SelectContent,
  SelectItem,
  SelectRoot,
  SelectTrigger,
  SelectValue,
} from '@civilgrid/ui/components';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { DateTime } from 'luxon';
import { useEffect, useState } from 'react';
import { type CreateUtilityCheckInput, createUtilityChecks, type ProjectUtilityCheck } from '~/api/civilsearch';
import { Notify } from '~/components2/Notify';

const ASSET_TYPES = ['storm', 'sewer', 'water', 'electric', 'gas'] as const;

const STATE_OPTIONS = [
  { value: 'gis', label: 'GIS' },
  { value: 'civilsearch', label: 'Civil Search' },
  { value: 'not_covered', label: 'Not Covered' },
  { value: 'other', label: 'Other' },
  { value: 'no_operator', label: 'No Operator' },
] as const;

interface AssetFormState {
  state: string;
  notes: string;
}

type FormState = Record<string, AssetFormState>;

function getInitialState(project: ProjectUtilityCheck): FormState {
  const formState: FormState = {};
  for (const assetType of ASSET_TYPES) {
    const existing = project.utilityChecks?.find((c) => c.assetType.toLowerCase() === assetType);
    formState[assetType] = {
      state: existing?.state ?? '',
      notes: existing?.notes ?? '',
    };
  }
  return formState;
}

export default function SSWEGCompletionForm({ project }: { project: ProjectUtilityCheck }) {
  const queryClient = useQueryClient();
  const [formState, setFormState] = useState<FormState>(() => getInitialState(project));

  // Reset form when project changes
  useEffect(() => {
    setFormState(getInitialState(project));
  }, [project]);

  const mutation = useMutation({
    mutationFn: (checks: CreateUtilityCheckInput[]) => createUtilityChecks(checks),
    onSuccess: () => {
      Notify.info({ message: 'Utility checks saved' });
      queryClient.invalidateQueries({ queryKey: ['project-utility-checks'] });
    },
    onError: () => {
      Notify.error({ message: 'Failed to save utility checks' });
    },
  });

  const initialState = getInitialState(project);

  function handleSubmit() {
    const checks: CreateUtilityCheckInput[] = ASSET_TYPES.filter((assetType) => {
      const curr = formState[assetType];
      const init = initialState[assetType];
      return curr.state !== init.state || curr.notes !== init.notes;
    }).map((assetType) => ({
      projectId: project.projectId,
      assetType,
      state: formState[assetType].state || undefined,
      notes: formState[assetType].notes,
    }));

    if (checks.length === 0) {
      Notify.info({ message: 'No changes to save' });
      return;
    }

    mutation.mutate(checks);
  }

  return (
    <div className="flex h-full flex-col gap-3 p-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium">SSWEG Completion — {project.projectName}</h3>
        <Button size="small" onClick={handleSubmit} disabled={mutation.isPending}>
          {mutation.isPending ? 'Saving...' : 'Save'}
        </Button>
      </div>
      <div className="grid grid-cols-5 gap-3">
        {ASSET_TYPES.map((assetType) => {
          const check = project.utilityChecks?.find((c) => c.assetType.toLowerCase() === assetType);

          const date = check ? DateTime.fromISO(check.createdAt).toLocaleString(DateTime.DATETIME_SHORT) : '';
          const by = check ? (check.createdByName ?? 'Unknown') : '';

          return (
            <div key={assetType} className="flex flex-col gap-1">
              <span className="text-xs font-medium text-grey-60 flex-row flex gap-1">
                {check?.state && <Icon icon={'check'} className="bg-green-50 rounded-full text-white" />}
                {assetType}
              </span>
              <SelectRoot
                value={formState[assetType].state || undefined}
                onValueChange={(value) =>
                  setFormState((prev) => ({
                    ...prev,
                    [assetType]: { ...prev[assetType], state: value },
                  }))
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select..." />
                </SelectTrigger>
                <SelectContent>
                  {STATE_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </SelectRoot>
              <textarea
                className="w-full resize-none rounded border border-grey-30 px-2 py-1 text-xs text-grey-80 placeholder:text-grey-50 focus:border-focus focus:outline-none"
                placeholder="Notes..."
                rows={4}
                value={formState[assetType].notes}
                onChange={(e) =>
                  setFormState((prev) => ({
                    ...prev,
                    [assetType]: { ...prev[assetType], notes: e.target.value },
                  }))
                }
              />

              {check && (
                <span className="text-xs text-grey-50">
                  Last checked by {by} on {date}
                </span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
